import { useQuery } from '@tanstack/react-query';
import { fetchAll } from '../../services/helpers';

type LandingPage = {
  id: number;
  name: string;
  status: number;
  domain: string;
  lastEdited: string; // ISO date string
  zuulUser: {
    id: number;
    email: string;
    realname: string;
    googleId: string | null;
    googleAccessToken: string | null;
    googleImageURL: string | null;
    lastLogin: string; // ISO date string
    online: boolean;
    failedLogins: number;
    zuulUserRole: {
      id: number;
      name: string;
      zuulPermissions: {
        id: number;
        offers: boolean;
        actions: boolean;
        sites: boolean;
      };
    };
  } | null;
  default: boolean;
  active: boolean;
};

type LandingPageDto = {
  id: number;
  layout: {
    id: number;
    name: string;
  };
  container: {
    id: number;
    name: string;
  };
  offer: {
    id: number;
    name: string;
    offerType: {
      id: number;
      name: string;
    };
    isPrimaryVariant: boolean;
    isActive: boolean;
    advertiser: {
      id: number;
      name: string;
      url: string;
      contactFirstName: string;
      contactLastName: string;
      contactEmail: string;
      contactPhone: string;
      address1: string;
      address2: string;
      city: string;
      state: string;
      country: string;
      phone1: string;
      phone2: string;
      phone3: string;
      zip: string;
      notes: string | null;
      ariaAdvertiserId: number;
    };
  };
  lastEdited: string; // ISO date string
  zuulUser: {
    id: number;
    email: string;
    realname: string;
    googleId: string | null;
    googleAccessToken: string | null;
    googleImageURL: string | null;
    lastLogin: string; // ISO date string
    online: boolean;
    failedLogins: number;
    zuulUserRole: {
      id: number;
      name: string;
      zuulPermissions: {
        id: number;
        offers: boolean;
        actions: boolean;
        sites: boolean;
      };
    };
  } | null;
};

export const useLandingPages = () =>
  useQuery<LandingPage[], Error>({
    queryKey: ['landingPages'],
    queryFn: () => fetchAll('landing-pages'),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

export const useLandingPagesDtos = <T = LandingPageDto[]>(selectFn?: (data: LandingPageDto[]) => T) => {
  const queryOptions: {
    queryKey: string[];
    queryFn: () => Promise<LandingPageDto[]>;
    refetchOnWindowFocus: false;
    refetchOnReconnect: false;
    select?: (data: LandingPageDto[]) => T;
  } = {
    queryKey: ['landingPagesDtos'],
    queryFn: () => fetchAll('landing-pages/dto'),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  };

  if (selectFn) {
    queryOptions.select = selectFn;
  }

  return useQuery<LandingPageDto[], Error, T>(queryOptions);
};
